import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CreditCard,
  MapPin,
  ShieldCheck,
  Tag,
  Truck,
} from "lucide-react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { getErrorMessage, formatCurrency } from "../core/api.jsx";
import {
  addCustomerAddress,
  getCustomerAddresses,
} from "../core/services/customerService";
import {
  applyCoupon as applyCouponCode,
  createCodOrder,
  createOrderAfterPayment,
  createPaymentOrder,
  verifyPayment,
} from "../core/services/checkoutService";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { useToast } from "../contexts/ToastContext";

const blankAddress = {
  fullName: "",
  phoneNo: "",
  address: "",
  city: "",
  state: "",
  country: "India",
  pinCode: "",
  landmark: "",
  label: "home",
  isDefault: false,
};
const addressTypeName = (value) =>
  ({ home: "Home", work: "Work", other: "Other" })[
    String(value || "").trim().toLowerCase()
  ] || "Saved address";
const orderItemsFrom = (items) =>
  items.map((item) => ({
    product: item.productId,
    quantity: item.quantity,
    variantId: item.variantId,
    colorId: item.colorId,
  }));
const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

export default function Checkout() {
  const { onLogin, settings } = useOutletContext();
  const { token, user } = useAuth();
  const { cartItems, cartSubtotal, cartSyncing, clearCart } = useCart();
  const { notify } = useToast();
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState([]);
  const [form, setForm] = useState({
    ...blankAddress,
    fullName: user?.name || "",
    phoneNo: user?.phone || "",
  });
  const [selectedAddress, setSelectedAddress] = useState("");
  const [useNewAddress, setUseNewAddress] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState("cod");
  const [couponText, setCouponText] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const orderSettings = settings?.orders || {};
  const subtotal = cartSubtotal;
  const defaultShippingCharge = Number(orderSettings.defaultShippingCharge || 0);
  const shipping = cartItems.reduce(
    (total, item) => {
      const productShipping = Number(item.product?.shippingCharge);
      const shippingPerUnit =
        Number.isFinite(productShipping) && productShipping > 0
          ? productShipping
          : defaultShippingCharge;
      return total + shippingPerUnit * item.quantity;
    },
    0,
  );
  const discount = Number(coupon?.discountAmount || 0);
  const tax =
    Math.round(
      Math.max(0, subtotal - discount) * Number(orderSettings.gstPercent || 0),
    ) / 100;
  const total = Math.max(0, subtotal - discount) + tax + shipping;

  useEffect(() => {
    if (!token) return;
    let alive = true;
    getCustomerAddresses()
      .then((data) => {
        if (!alive) return;
        const saved = data.addresses || [];
        setAddresses(saved);
        const preferred =
          saved.find((address) => address.isDefault) || saved[0];
        if (preferred) {
          setSelectedAddress(preferred._id);
          setUseNewAddress(false);
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [token]);

  const activeAddress = useMemo(
    () =>
      useNewAddress
        ? form
        : addresses.find((item) => item._id === selectedAddress),
    [useNewAddress, form, addresses, selectedAddress],
  );
  const updateField = (event) =>
    setForm((current) => ({
      ...current,
      [event.target.name]: event.target.value,
    }));

  const applyCoupon = async () => {
    if (!token) return onLogin?.();
    setBusy(true);
    setError("");
    try {
      const data = await applyCouponCode(couponText.trim());
      setCoupon(data.coupon);
      notify("Coupon applied to your order.");
    } catch (requestError) {
      setCoupon(null);
      setError(getErrorMessage(requestError));
    } finally {
      setBusy(false);
    }
  };

  const getShippingInfo = async () => {
    if (!activeAddress) throw new Error("Choose or add a delivery address.");
    const required = [
      "fullName",
      "phoneNo",
      "address",
      "city",
      "state",
      "country",
      "pinCode",
    ];
    if (required.some((key) => !String(activeAddress[key] || "").trim()))
      throw new Error("Please complete all required delivery address fields.");
    if (
      useNewAddress &&
      !addresses.some(
        (address) =>
          address.address === form.address && address.pinCode === form.pinCode,
      )
    ) {
      const data = await addCustomerAddress({
        ...form,
        isDefault: addresses.length === 0,
      });
      setAddresses(data.addresses || []);
    }
    return {
      fullName: activeAddress.fullName,
      phoneNo: activeAddress.phoneNo,
      address: activeAddress.address,
      city: activeAddress.city,
      state: activeAddress.state,
      country: activeAddress.country,
      pinCode: activeAddress.pinCode,
      landmark: activeAddress.landmark || "",
    };
  };

  const placeOrder = async (event) => {
    event.preventDefault();
    if (!token) return onLogin?.();
    if (!cartItems.length) return navigate("/cart");
    setBusy(true);
    setError("");
    try {
      const shippingInfo = await getShippingInfo();
      const orderItems = orderItemsFrom(cartItems);
      if (paymentMethod === "cod") {
        const data = await createCodOrder({
          shippingInfo,
          orderItems,
          couponCode: coupon?.code,
        });
        clearCart();
        navigate("/orders", { state: { placedOrder: data.order } });
        notify("Your order has been placed. Thank you!");
        return;
      }
      const loaded = await loadRazorpay();
      if (!loaded)
        throw new Error(
          "Couldn't load secure checkout. Check your connection and try again.",
        );
      const payment = await createPaymentOrder(coupon?.code);
      if (!payment.keyId || !payment.order?.id)
        throw new Error(
          "Online payment is not configured for this store. Please choose cash on delivery.",
        );
      const options = {
        key: payment.keyId,
        amount: payment.order.amount,
        currency: payment.order.currency || "INR",
        name: settings?.general?.appName || "Sugam Store",
        description: "Your Sugam Store order",
        order_id: payment.order.id,
        prefill: {
          name: shippingInfo.fullName,
          contact: shippingInfo.phoneNo,
          email: user?.email || "",
        },
        theme: { color: "#719a61" },
        handler: async (response) => {
          try {
            const paymentInfo = {
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            };
            await verifyPayment(paymentInfo);
            const data = await createOrderAfterPayment({
              shippingInfo,
              orderItems,
              paymentInfo,
              couponCode: coupon?.code,
            });
            clearCart();
            notify("Payment complete. Your order is confirmed.");
            navigate("/orders", { state: { placedOrder: data.order } });
          } catch (requestError) {
            setError(
              getErrorMessage(
                requestError,
                "Payment could not be confirmed. Contact support before retrying.",
              ),
            );
          } finally {
            setBusy(false);
          }
        },
        modal: { ondismiss: () => setBusy(false) },
      };
      const checkout = new window.Razorpay(options);
      checkout.on("payment.failed", (response) => {
        setBusy(false);
        setError(
          response.error?.description ||
            "Payment didn't go through. Please try again.",
        );
      });
      checkout.open();
      return;
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    }
    setBusy(false);
  };

  if (!cartItems.length)
    return (
      <main className="page-shell">
        <div className="empty-state">
          <span className="empty-state__icon">
            <Truck />
          </span>
          <h2>Your bag is empty.</h2>
          <p>Add a few favourites before checking out.</p>
          <Link className="button button--primary" to="/shop">
            Browse the shop <ArrowRight size={17} />
          </Link>
        </div>
      </main>
    );
  if (!token)
    return (
      <main className="page-shell">
        <div className="empty-state">
          <span className="empty-state__icon">
            <ShieldCheck />
          </span>
          <h2>Sign in to finish your order.</h2>
          <p>Your cart will be right here when you return.</p>
          <button className="button button--primary" onClick={onLogin}>
            Sign in securely <ArrowRight size={17} />
          </button>
          <Link className="text-link" to="/cart">
            <ArrowLeft size={16} /> Back to bag
          </Link>
        </div>
      </main>
    );
  return (
    <main className="page-shell content-page checkout-page">
      <div className="breadcrumbs">
        <Link to="/">Home</Link>
        <span>/</span>
        <Link to="/cart">Your bag</Link>
        <span>/</span>
        <span>Checkout</span>
      </div>
      <div className="page-title-row">
        <div>
          <p className="eyebrow">Almost yours</p>
          <h1>Secure checkout</h1>
        </div>
        <span className="secure-label">
          <ShieldCheck size={16} /> Secure checkout
        </span>
      </div>
      <form className="checkout-layout" onSubmit={placeOrder}>
        <div className="checkout-main">
          <section className="checkout-card">
            <div className="checkout-card__heading">
              <span className="step-number">01</span>
              <div>
                <h2>Delivery details</h2>
                <p>Where should we bring your order?</p>
              </div>
              <MapPin size={19} />
            </div>
            {addresses.length > 0 && (
              <div className="address-options">
                {addresses.map((address) => (
                  <button
                    type="button"
                    key={address._id}
                    className={`address-option${!useNewAddress && selectedAddress === address._id ? " is-selected" : ""}`}
                    onClick={() => {
                      setSelectedAddress(address._id);
                      setUseNewAddress(false);
                    }}
                  >
                    <span className="radio-dot" />
                    <span>
                      <strong>{addressTypeName(address.label)}</strong>
                      <small>
                        {address.fullName} · {address.address}, {address.city},{" "}
                        {address.pinCode}
                      </small>
                    </span>
                    {address.isDefault && <em>Default</em>}
                  </button>
                ))}
                <button
                  type="button"
                  className={`address-option address-option--new${useNewAddress ? " is-selected" : ""}`}
                  onClick={() => setUseNewAddress(true)}
                >
                  <span className="radio-dot" />
                  <span>
                    <strong>Deliver to a new address</strong>
                  </span>
                </button>
              </div>
            )}
            {useNewAddress && (
              <div className="address-form">
                <label className="field field--wide">
                  <span>Address type</span>
                  <select
                    name="label"
                    value={form.label}
                    onChange={updateField}
                  >
                    <option value="home">Home</option>
                    <option value="work">Work</option>
                    <option value="other">Other</option>
                  </select>
                </label>
                <label className="field">
                  <span>Full name *</span>
                  <input
                    name="fullName"
                    autoComplete="name"
                    value={form.fullName}
                    onChange={updateField}
                    required
                  />
                </label>
                <label className="field">
                  <span>Mobile number *</span>
                  <input
                    name="phoneNo"
                    inputMode="tel"
                    autoComplete="tel"
                    value={form.phoneNo}
                    onChange={updateField}
                    required
                  />
                </label>
                <label className="field field--wide">
                  <span>Street address *</span>
                  <input
                    name="address"
                    autoComplete="street-address"
                    value={form.address}
                    onChange={updateField}
                    required
                  />
                </label>
                <label className="field">
                  <span>City *</span>
                  <input
                    name="city"
                    autoComplete="address-level2"
                    value={form.city}
                    onChange={updateField}
                    required
                  />
                </label>
                <label className="field">
                  <span>State *</span>
                  <input
                    name="state"
                    autoComplete="address-level1"
                    value={form.state}
                    onChange={updateField}
                    required
                  />
                </label>
                <label className="field">
                  <span>PIN code *</span>
                  <input
                    name="pinCode"
                    inputMode="numeric"
                    autoComplete="postal-code"
                    value={form.pinCode}
                    onChange={updateField}
                    required
                  />
                </label>
                <label className="field">
                  <span>Landmark</span>
                  <input
                    name="landmark"
                    value={form.landmark}
                    onChange={updateField}
                  />
                </label>
              </div>
            )}
          </section>
          <section className="checkout-card">
            <div className="checkout-card__heading">
              <span className="step-number">02</span>
              <div>
                <h2>Payment method</h2>
                <p>Choose how you’d like to pay.</p>
              </div>
              <CreditCard size={19} />
            </div>
            <div className="payment-options">
              {orderSettings.allowCod !== false && (
                <button
                  type="button"
                  className={`payment-option${paymentMethod === "cod" ? " is-selected" : ""}`}
                  onClick={() => setPaymentMethod("cod")}
                >
                  <span className="radio-dot" />
                  <span>
                    <strong>Cash on delivery</strong>
                    <small>Pay when your order arrives</small>
                  </span>
                  <Truck size={19} />
                </button>
              )}
              {orderSettings.allowRazorpay !== false && (
                <button
                  type="button"
                  className={`payment-option${paymentMethod === "online" ? " is-selected" : ""}`}
                  onClick={() => setPaymentMethod("online")}
                >
                  <span className="radio-dot" />
                  <span>
                    <strong>Pay online</strong>
                    <small>UPI, cards, net banking and more</small>
                  </span>
                  <CreditCard size={19} />
                </button>
              )}
              {orderSettings.allowCod === false &&
                orderSettings.allowRazorpay === false && (
                  <p className="form-error">
                    Online ordering is temporarily unavailable. Please contact
                    the store.
                  </p>
                )}
            </div>
          </section>
          {error && <p className="inline-alert form-error">{error}</p>}
        </div>
        <aside className="order-summary checkout-summary">
          <p className="eyebrow">Your order</p>
          <h2>Order summary</h2>
          <div className="checkout-mini-items">
            {cartItems.map((item) => (
              <div className="checkout-mini-item" key={item.key || `${item.productId}:${item.variantId}`}>
                <span>
                  {item.name}
                  {item.variantName ? <em> · {item.variantName}</em> : null}{" "}
                  <em>× {item.quantity}</em>
                </span>
                <strong>{formatCurrency(item.price * item.quantity)}</strong>
              </div>
            ))}
          </div>
          <div className="coupon-box">
            <label htmlFor="coupon-code">
              <Tag size={16} /> Have a coupon?
            </label>
            <div>
              <input
                id="coupon-code"
                value={couponText}
                onChange={(event) => setCouponText(event.target.value)}
                placeholder="Enter code"
                disabled={Boolean(coupon) || cartSyncing}
              />
              <button
                type="button"
                disabled={
                  busy || cartSyncing || Boolean(coupon) || !couponText.trim()
                }
                onClick={applyCoupon}
              >
                {coupon ? <Check size={16} /> : "Apply"}
              </button>
            </div>
            {coupon && (
              <button
                type="button"
                className="text-button"
                onClick={() => setCoupon(null)}
              >
                Remove {coupon.code}
              </button>
            )}
          </div>
          <div className="summary-line">
            <span>Subtotal</span>
            <strong>{formatCurrency(subtotal)}</strong>
          </div>
          {discount > 0 && (
            <div className="summary-line summary-line--discount">
              <span>Discount</span>
              <strong>− {formatCurrency(discount)}</strong>
            </div>
          )}
          <div className="summary-line">
            <span>Shipping</span>
            <strong>{shipping ? formatCurrency(shipping) : "Free"}</strong>
          </div>
          {tax > 0 && (
            <div className="summary-line">
              <span>Tax</span>
              <strong>{formatCurrency(tax)}</strong>
            </div>
          )}
          <div className="summary-total">
            <span>Total</span>
            <strong>{formatCurrency(total)}</strong>
          </div>
          <button
            className="button button--primary button--full"
            type="submit"
            disabled={
              busy ||
              cartSyncing ||
              (orderSettings.allowCod === false &&
                orderSettings.allowRazorpay === false)
            }
          >
            {cartSyncing ? (
              "Syncing your bag…"
            ) : busy ? (
              "Processing…"
            ) : (
              <>
                Place order <ArrowRight size={17} />
              </>
            )}
          </button>
          <p className="summary-note">
            <ShieldCheck size={15} /> Your details are protected and your
            payment is secure.
          </p>
          <Link to="/cart" className="text-link">
            <ArrowLeft size={15} /> Back to your bag
          </Link>
        </aside>
      </form>
    </main>
  );
}
